import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import api from '../../../utils/api';
import BatchSelector from '../../../Components/BatchSelector';

// Versi mentor dari Pages/Admin/Attendances/AttendancesList.jsx — daftar per
// sesi (bukan ringkasan per peserta seperti versi lama), dibatasi backend ke
// divisi yang dibimbing mentor ini saja.
const AttendancesByMentor = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get('limit')) || 25);
  const [filterDate, setFilterDate] = useState(searchParams.get('date') || format(new Date(), 'yyyy-MM-dd'));
  const [batchId, setBatchId] = useState(searchParams.get('batchId') || '');

  const [attendances, setAttendances] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAttendances = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getMentorAttendances({
        date: filterDate,
        batchId,
        page: currentPage,
        limit: itemsPerPage,
      });
      setAttendances(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [filterDate, batchId, currentPage, itemsPerPage]);

  useEffect(() => {
    setSearchParams({
      date: filterDate,
      batchId: batchId || '',
      page: currentPage,
      limit: itemsPerPage,
    });
    fetchAttendances();
  }, [filterDate, batchId, currentPage, itemsPerPage, fetchAttendances, setSearchParams]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber && pageNumber !== currentPage) setCurrentPage(pageNumber);
  };

  const handlePageSizeChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ON_TIME':
        return <span className='badge bg-success bg-opacity-10 text-success rounded-pill px-3 py-2'>Hadir</span>;
      case 'LATE_TOLERANCE':
        return <span className='badge bg-info bg-opacity-10 text-info rounded-pill px-3 py-2'>Hadir (Toleransi)</span>;
      case 'PENDING_APPROVAL':
        return <span className='badge bg-warning bg-opacity-10 text-warning rounded-pill px-3 py-2'>Menunggu Persetujuan</span>;
      case 'ABSENT':
        return <span className='badge bg-secondary bg-opacity-10 text-secondary rounded-pill px-3 py-2'>Belum Absen</span>;
      case 'LATE':
        return <span className='badge bg-danger bg-opacity-10 text-danger rounded-pill px-3 py-2'>Terlambat</span>;
      default:
        return <span className='badge bg-secondary bg-opacity-10 text-secondary rounded-pill px-3 py-2'>{status || 'UNKNOWN'}</span>;
    }
  };

  return (
    <div className='card card-body border-0 shadow-sm rounded-4'>
      {/* Header */}
      <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <div
            className='bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3'
            style={{ width: '40px', height: '40px' }}
          >
            <i className='bi bi-calendar-check text-primary fs-5'></i>
          </div>
          <div>
            <h1 className='h5 fw-bold mb-0 text-dark'>Absensi Mentee</h1>
            <p className='text-muted mb-0 d-none d-lg-block' style={{ fontSize: '0.75rem' }}>
              Pantau absensi, logbook, dan pengajuan toleransi mentee Anda.
            </p>
          </div>
        </div>
        <div className='d-flex flex-wrap align-items-center gap-3 ms-md-auto'>
          <div className='d-flex align-items-center'>
            <label className='me-2 fw-medium text-muted small mb-0 d-none d-xl-inline'>Batch:</label>
            <div style={{ minWidth: '150px' }}>
              <BatchSelector
                value={batchId}
                onChange={(val) => {
                  setBatchId(val);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className='row g-3 mb-4 align-items-center'>
        <div className='col-md-6 col-lg-8'>
          <div className='d-flex align-items-center gap-2'>
            <div className='input-group input-group-sm w-auto rounded-pill shadow-none border-0 bg-light px-3 py-1'>
              <span className='input-group-text bg-transparent border-0 text-muted'>
                <i className='bi bi-calendar-event'></i>
              </span>
              <input
                type='date'
                className='form-control bg-transparent border-0 shadow-none extra-small fw-bold text-dark'
                value={filterDate}
                onChange={(e) => {
                  setFilterDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>
        <div className='col-md-6 col-lg-4 d-flex justify-content-md-end gap-3 align-items-center'>
          <div className='d-flex align-items-center'>
            <label className='me-2 small text-muted'>Tampilkan:</label>
            <select
              className='form-select form-select-sm rounded-pill shadow-sm border-0 bg-light px-3'
              value={itemsPerPage}
              onChange={handlePageSizeChange}
              style={{ width: '80px', fontSize: '0.8rem' }}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className='table-responsive'>
            <table className='table table-hover align-middle mb-0'>
              <thead className='bg-light text-muted small text-uppercase fw-bold'>
                <tr>
                  <th className='ps-4 py-3 border-0' style={{ width: '60px' }}>#</th>
                  <th className='border-0 py-3'>Nama Mentee</th>
                  <th className='border-0 py-3 text-center'>Sesi Tanggal</th>
                  <th className='border-0 py-3 text-center'>Status Masuk</th>
                  <th className='border-0 py-3 text-center'>Status Keluar</th>
                  <th className='border-0 py-3 text-end pe-4'>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx} className='placeholder-glow'>
                      <td className='ps-4 py-3'><span className='placeholder col-6 rounded'></span></td>
                      <td className='py-3'><span className='placeholder col-10 rounded'></span></td>
                      <td className='py-3 text-center'><span className='placeholder col-8 rounded'></span></td>
                      <td className='py-3 text-center'><span className='placeholder col-6 rounded'></span></td>
                      <td className='py-3 text-center'><span className='placeholder col-6 rounded'></span></td>
                      <td className='text-end pe-4 py-3'><span className='placeholder col-4 rounded py-3'></span></td>
                    </tr>
                  ))
                ) : attendances?.result?.length > 0 ? (
                  attendances.result.map((item, idx) => (
                    <tr
                      key={item.id}
                      className='cursor-pointer transition-all'
                      onClick={() => navigate(`/mentor/attendances/${item.id}`)}
                    >
                      <td className='ps-4 py-3 text-muted small'>{(currentPage - 1) * itemsPerPage + (idx + 1)}</td>
                      <td className='py-3'>
                        <div className='fw-bold text-dark'>{item.firstName} {item.lastName}</div>
                      </td>
                      <td className='py-3 text-center'>
                        <span className='extra-small fw-medium text-muted bg-light px-2 py-1 rounded'>
                          {item.sessionDate ? format(new Date(item.sessionDate), 'dd MMM yyyy', { locale: localeId }) : '-'}
                        </span>
                      </td>
                      <td className='py-3 text-center'>
                        <div className='d-flex flex-column align-items-center gap-1'>
                          {getStatusBadge(item.clockInStatus)}
                          {item.clockInAt && (
                            <span className='extra-small text-muted fw-medium'>{format(new Date(item.clockInAt), 'HH:mm')}</span>
                          )}
                        </div>
                      </td>
                      <td className='py-3 text-center'>
                        {item.clockOutStatus ? (
                          <div className='d-flex flex-column align-items-center gap-1'>
                            {getStatusBadge(item.clockOutStatus)}
                            {item.clockOutAt && (
                              <span className='extra-small text-muted fw-medium'>{format(new Date(item.clockOutAt), 'HH:mm')}</span>
                            )}
                          </div>
                        ) : (
                          <span className='text-muted extra-small'>-</span>
                        )}
                      </td>
                      <td className='py-3 text-end pe-4'>
                        <button className='btn btn-light btn-sm rounded-pill shadow-none border border-light-subtle extra-small fw-bold px-3'>
                          Detail <i className='bi bi-chevron-right ms-1'></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan='6' className='text-center py-5'>
                      <div className='text-muted mb-3 opacity-25'>
                        <i className='bi bi-calendar-x fs-1'></i>
                      </div>
                      <h6 className='fw-bold text-dark mb-1'>Belum ada data absensi</h6>
                      <p className='text-muted small mb-0'>Belum ada data rekaman absensi mentee Anda untuk saat ini.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!isLoading && attendances?.meta?.pages > 1 && (
            <div className='mt-4 pt-4 border-top d-flex flex-column flex-md-row align-items-center justify-content-between gap-3'>
              <p className='text-muted extra-small mb-0'>
                Halaman <span className='fw-bold text-dark'>{attendances.meta.currentPage}</span> dari{' '}
                <span className='fw-bold text-dark'>{attendances.meta.pages}</span>
              </p>
              <nav aria-label='Page navigation'>
                <ul className='pagination pagination-sm justify-content-center mb-0 gap-2'>
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className='page-link rounded-pill border-0 bg-light text-dark px-3 fw-bold'
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      <i className='bi bi-chevron-left'></i>
                    </button>
                  </li>
                  <li className='page-item active'>
                    <span className='page-link rounded-pill border-0 bg-primary text-white px-3 fw-bold shadow-sm'>{currentPage}</span>
                  </li>
                  <li className={`page-item ${currentPage === attendances.meta.pages ? 'disabled' : ''}`}>
                    <button
                      className='page-link rounded-pill border-0 bg-light text-dark px-3 fw-bold'
                      disabled={currentPage === attendances.meta.pages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      <i className='bi bi-chevron-right'></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}

      <style>{`
        .extra-small { font-size: 0.7rem; }
        .transition-all { transition: all 0.2s ease; }
        .table-hover tbody tr:hover {
          background-color: rgba(var(--bs-primary-rgb), 0.03) !important;
          transform: scale(0.998);
        }
      `}</style>
    </div>
  );
};

export default AttendancesByMentor;
